// Notification sound registry for Darshan.
//
// Each sound gets its OWN Android notification channel because a channel's sound
// is locked the moment it is created — the only way to switch sounds is to send
// notifications to a different channel. The channel ids here must stay in sync
// with the backend (backend/src/services/pushNotificationService.ts).
//
// Adding / replacing a sound:
//   1. Drop the audio file in BOTH:
//        frontend/android/app/src/main/res/raw/<file>      (Android, background)
//        frontend/public/sounds/<file>                     (in-app, foreground)
//      Android raw resource names must be lowercase a-z, 0-9, `_` and start with
//      a letter. Keep the same name in both folders. .wav or .mp3 both work.
//   2. Add an entry below. If you REPLACE an existing file with a new sound,
//      bump its `channelId` version (…_v1 -> …_v2) so Android picks it up.
//   3. Mirror the id -> channelId map in the backend service.
//   4. Rebuild:  cd frontend && npm run android:sync

export interface NotificationSound {
  id: string;
  label: string;
  /** file name in res/raw and public/sounds */
  file: string;
  /** Android notification channel id (versioned) */
  channelId: string;
}

export const NOTIFICATION_SOUNDS: NotificationSound[] = [
  { id: "chime", label: "Chime", file: "notify_chime.wav", channelId: "alerts_chime_v1" },
  { id: "siren", label: "Emergency siren", file: "notify_siren.wav", channelId: "alerts_siren_v1" },
  { id: "alarm", label: "Alarm beeps", file: "notify_alarm.wav", channelId: "alerts_alarm_v1" },
  { id: "bell", label: "Bell", file: "notify_bell.wav", channelId: "alerts_bell_v1" },
  { id: "pager", label: "Pager", file: "notify_pager.wav", channelId: "alerts_pager_v1" },
];

export const DEFAULT_SOUND_ID = "chime";

const STORAGE_KEY = "darshan-notification-sound";

export function getSoundById(id: string | null | undefined): NotificationSound {
  return NOTIFICATION_SOUNDS.find((s) => s.id === id) ?? NOTIFICATION_SOUNDS[0];
}

export function getSelectedSoundId(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && NOTIFICATION_SOUNDS.some((s) => s.id === stored)) return stored;
  } catch {
    // ignore
  }
  return DEFAULT_SOUND_ID;
}

export function setSelectedSoundId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore
  }
}

export function getSelectedSound(): NotificationSound {
  return getSoundById(getSelectedSoundId());
}

export function soundFileUrl(sound: NotificationSound): string {
  return `/sounds/${sound.file}`;
}

let audio: HTMLAudioElement | null = null;
let lastAlertAt = 0;
const ALERT_COOLDOWN_MS = 4000;

/** Alert tone for an incoming event. A new booking reaches a foregrounded app both as a push and
 *  as a pending-bookings refresh, so a second alert within a few seconds is the same event - skip it. */
export function playAlertSound(): void {
  const now = Date.now();
  if (now - lastAlertAt < ALERT_COOLDOWN_MS) return;
  lastAlertAt = now;
  playNotificationSound();
}

/** Play a notification tone. `id` defaults to the user's selected sound.
 *  Failures (autoplay blocked, missing file) are swallowed. */
export function playNotificationSound(id?: string): void {
  if (typeof Audio === "undefined") return;
  const sound = getSoundById(id ?? getSelectedSoundId());
  try {
    if (!audio) audio = new Audio();
    audio.src = soundFileUrl(sound);
    audio.volume = 0.5;
    audio.currentTime = 0;
    void audio.play().catch(() => undefined);
  } catch {
    // ignore
  }
}
